import React, { useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm, useFieldArray, useWatch, Controller } from "react-hook-form";
import Select from "react-select";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getPartyOptions,
  getItemOptions,
  fetchPurchasePartiesOptions,
  fetchPurchaseItemsOptions,
} from "../../features/purchase/purchaseSlice";

import { IPurchaseFormInput, PurchaseOptionsProps } from "../../types/purchase";
import { FaPlus, FaCalendarAlt } from "react-icons/fa";
import { GiCancel } from "react-icons/gi";
import { selectStyles } from "../../static/styles";

type Props = {
  defaultValues?: IPurchaseFormInput;
  defaultPartyOption?: PurchaseOptionsProps | null;
  onSubmit: (data: IPurchaseFormInput) => void;
  isSubmitting: boolean;
  onCancel: () => void;
};

const PurchaseForm = ({
  defaultValues,
  defaultPartyOption,
  onSubmit,
  isSubmitting,
  onCancel,
}: Props) => {
  const dispatch = useAppDispatch();
  const partyOptions = useAppSelector(getPartyOptions);
  const itemOptions = useAppSelector(getItemOptions);

  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState: { errors },
  } = useForm<IPurchaseFormInput>({
    defaultValues: defaultValues || {
      party_id: 0,
      date: new Date().toISOString().split("T")[0],
      voucher_no: "",
      discount: "" as any,
      tax: "" as any,
      items: [{ item_id: 0, quantity: "" as any, rate: "" as any }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const watchedItems = useWatch({ control, name: "items" });
  const watchedDiscount = useWatch({ control, name: "discount" });
  const watchedTax = useWatch({ control, name: "tax" });

  const subTotal = useMemo(() => {
    if (!watchedItems) return 0;
    return watchedItems.reduce((sum, item) => {
      const qty = Number(item?.quantity) || 0;
      const rate = Number(item?.rate) || 0;
      return sum + qty * rate;
    }, 0);
  }, [watchedItems]);

  const grandTotal = useMemo(() => {
    const discount = Number(watchedDiscount) || 0;
    const tax = Number(watchedTax) || 0;
    return subTotal - discount + tax;
  }, [subTotal, watchedDiscount, watchedTax]);

  const [selectedParty, setSelectedParty] = useState<PurchaseOptionsProps | null>(
    defaultPartyOption || null
  );

  useEffect(() => {
    dispatch(fetchPurchasePartiesOptions());
    dispatch(fetchPurchaseItemsOptions());
  }, [dispatch]);

  useEffect(() => {
    if (defaultPartyOption) {
      setSelectedParty(defaultPartyOption);
    }
  }, [defaultPartyOption]);

  const [partyError, setPartyError] = useState(false);
  const [itemErrors, setItemErrors] = useState<number[]>([]);

  const handleOnSubmit: SubmitHandler<IPurchaseFormInput> = async (data) => {
    let hasError = false;

    if (!selectedParty) {
      setPartyError(true);
      hasError = true;
    } else {
      setPartyError(false);
    }

    const invalidItems: number[] = [];
    data.items.forEach((item, index) => {
      if (!item.item_id || item.item_id === 0) {
        invalidItems.push(index);
        hasError = true;
      }
    });
    setItemErrors(invalidItems);

    if (hasError) return;

    onSubmit({
      ...data,
      party_id: selectedParty?.value || data.party_id,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)}>
      <div className="grid gap-4">
        {/* Header Row: Party (60%) + Voucher No (20%) + Date (20%) */}
        <div className="grid grid-cols-1 md:grid-cols-10 gap-4">
          {/* Party Selection */}
          <div className="md:col-span-6">
            <label className="text-primary-1 font-bold">પાર્ટી :</label>
            <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-[44px] w-full relative rounded">
              <Select
                options={partyOptions}
                value={selectedParty}
                onChange={(option: any) => {
                  setSelectedParty(option);
                  setValue("party_id", option?.value || 0);
                }}
                placeholder="પાર્ટી પસંદ કરો"
                isClearable
                className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                styles={selectStyles}
              />
            </div>
            {partyError && !selectedParty && (
              <span className="text-red-500">પાર્ટી પસંદ કરો</span>
            )}
          </div>

          {/* Voucher No */}
          <div className="md:col-span-2">
            <label className="text-primary-1 font-bold">વાઉચર નંબર :</label>
            <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded p-[2px] h-[44px]">
              <input
                className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 w-full h-full rounded focus:outline-none"
                type="text"
                placeholder="વાઉચર નંબર"
                {...register("voucher_no")}
              />
            </div>
          </div>

          {/* Date */}
          <div className="md:col-span-2">
            <label className="text-primary-1 font-bold">તારીખ :</label>
            <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded pr-[2px] h-[44px]">
              <FaCalendarAlt className="text-white mx-2" />
              <input
                className="bg-white text-primary-1 placeholder:text-primary-2 font-semibold text-xl focus:outline-none p-2 rounded flex-grow h-[40px]"
                type="date"
                {...register("date", { required: true })}
              />
            </div>
            {errors.date && errors.date.type === "required" && (
              <span className="text-red-500">તારીખ દાખલ કરો</span>
            )}
          </div>
        </div>

        {/* Items Section */}
        <div>
          <label className="text-primary-1 font-bold mb-2 block">આઈટમ્સ :</label>

          {fields.length <= 0 && (
            <h2 className="text-primary-2 font-bold text-xl text-center py-2">
              કોઈ આઈટમ મળી નથી, નવી ઉમેરવા માટે આઈટમ ઉંમેરો બટન પર ક્લિક કરો
            </h2>
          )}

          {fields.map((field, index) => (
            <div
              key={field.id}
              className="mb-4 border-2 border-primary-1 rounded-md p-4"
            >
              <h2 className="relative w-full text-2xl lg:text-xl underline text-primary-1 font-bold mb-4">
                આઈટમ - {index + 1}
                {fields.length > 1 && (
                  <GiCancel
                    onClick={() => remove(index)}
                    className="absolute cursor-pointer z-10 shadow bg-white top-1 right-1 text-3xl lg:text-xl text-primary-1 border-2 border-primary-1 p-1 lg:p-0 rounded-full"
                  />
                )}
              </h2>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-4">
                {/* Item Selection - 40% */}
                <div className="md:col-span-2">
                  <label className="text-primary-1 font-bold">આઈટમ :</label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-[44px] w-full relative rounded">
                    <Controller
                      name={`items.${index}.item_id`}
                      control={control}
                      render={({ field }) => (
                        <Select
                          options={itemOptions}
                          value={
                            itemOptions.find(
                              (opt) => opt.value === field.value
                            ) || null
                          }
                          onChange={(option: any) => {
                            field.onChange(option?.value || 0);
                            setItemErrors((prev) => prev.filter((i) => i !== index));
                          }}
                          placeholder="આઈટમ નું નામ દાખલ કરો"
                          isClearable
                          className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          styles={selectStyles}
                        />
                      )}
                    />
                  </div>
                  {itemErrors.includes(index) && (
                    <span className="text-red-500">આઈટમ પસંદ કરો</span>
                  )}
                </div>

                {/* Quantity - 20% */}
                <div>
                  <label className="text-primary-1 font-bold">જથ્થો :</label>
                  <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded p-[2px] h-[44px]">
                    <input
                      className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 w-full h-full rounded focus:outline-none"
                      type="number"
                      step="0.001"
                      placeholder="જથ્થો"
                      {...register(`items.${index}.quantity`, {
                        required: true,
                        valueAsNumber: true,
                      })}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                  </div>
                  {errors.items?.[index]?.quantity && (
                    <span className="text-red-500">જથ્થો દાખલ કરો</span>
                  )}
                </div>

                {/* Rate - 20% */}
                <div>
                  <label className="text-primary-1 font-bold">ભાવ :</label>
                  <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded p-[2px] h-[44px]">
                    <input
                      className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 w-full h-full rounded focus:outline-none"
                      type="number"
                      step="0.01"
                      placeholder="ભાવ"
                      {...register(`items.${index}.rate`, {
                        required: true,
                        valueAsNumber: true,
                      })}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                  </div>
                  {errors.items?.[index]?.rate && (
                    <span className="text-red-500">ભાવ દાખલ કરો</span>
                  )}
                </div>

                {/* Amount (calculated) - 20% */}
                <div>
                  <label className="text-primary-1 font-bold">રકમ :</label>
                  <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded p-[2px] h-[44px]">
                    <input
                      className="bg-gray-100 text-primary-1 px-3 py-1 font-semibold text-xl w-full h-full rounded focus:outline-none cursor-not-allowed text-right"
                      type="text"
                      value={(
                        (Number(watchedItems?.[index]?.quantity) || 0) *
                        (Number(watchedItems?.[index]?.rate) || 0)
                      ).toFixed(2)}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Add Item button - below items list */}
          <div className="text-center lg:text-right">
            <button
              type="button"
              onClick={() => append({ item_id: 0, quantity: "" as any, rate: "" as any })}
              className="mx-auto lg:mx-0 shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
            >
              <FaPlus className="inline mr-2" /> આઈટમ ઉંમેરો
            </button>
          </div>
        </div>

        {/* Summary Section - Right Aligned, same width as રકમ (1/5) */}
        <div className="grid gap-3">
          {/* Subtotal */}
          <div className="grid grid-cols-1 md:grid-cols-5">
            <div className="md:col-start-5">
              <label className="text-primary-1 font-bold">સબ ટોટલ :</label>
              <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded p-[2px] h-[44px]">
                <input
                  className="bg-gray-100 text-primary-1 px-3 py-1 font-semibold text-xl w-full h-full rounded focus:outline-none cursor-not-allowed text-right"
                  type="text"
                  value={subTotal.toFixed(2)}
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Discount */}
          <div className="grid grid-cols-1 md:grid-cols-5">
            <div className="md:col-start-5">
              <label className="text-primary-1 font-bold">ડિસ્કાઉન્ટ :</label>
              <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded p-[2px] h-[44px]">
                <input
                  className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 w-full h-full rounded focus:outline-none text-right"
                  type="number"
                  step="0.01"
                  placeholder="ડિસ્કાઉન્ટ"
                  {...register("discount", { valueAsNumber: true })}
                  onFocus={(e) =>
                    e.target.addEventListener(
                      "wheel",
                      function (e) {
                        e.preventDefault();
                      },
                      { passive: false }
                    )
                  }
                />
              </div>
            </div>
          </div>

          {/* Tax */}
          <div className="grid grid-cols-1 md:grid-cols-5">
            <div className="md:col-start-5">
              <label className="text-primary-1 font-bold">ટેક્સ :</label>
              <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded p-[2px] h-[44px]">
                <input
                  className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 w-full h-full rounded focus:outline-none text-right"
                  type="number"
                  step="0.01"
                  placeholder="ટેક્સ"
                  {...register("tax", { valueAsNumber: true })}
                  onFocus={(e) =>
                    e.target.addEventListener(
                      "wheel",
                      function (e) {
                        e.preventDefault();
                      },
                      { passive: false }
                    )
                  }
                />
              </div>
            </div>
          </div>

          {/* Grand Total */}
          <div className="grid grid-cols-1 md:grid-cols-5">
            <div className="md:col-start-5">
              <label className="text-primary-1 font-bold">ગ્રાન્ડ ટોટલ :</label>
              <div className="flex items-center bg-gradient-to-r from-primary-1 to-primary-2 rounded p-[2px] h-[44px]">
                <input
                  className="bg-gray-100 text-primary-1 px-3 py-1 font-semibold text-xl w-full h-full rounded focus:outline-none cursor-not-allowed text-right"
                  type="text"
                  value={grandTotal.toFixed(2)}
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="grid lg:flex gap-3 mt-3 justify-center lg:justify-end">
          <div className="text-center">
            <button
              className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              બંધ કરો
            </button>
          </div>

          <div className="text-center">
            <button
              className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "રાહ જુઓ..." : "આગળ વધો"}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default PurchaseForm;
